import { LOCALSTORAGE_ACCESS_TOKEN } from "./constants/localStorageConstantes";
import axios from "axios";
import { showErrorToast } from "../components/common/toast/toastService";
function normalizeBaseUrl(url) {
    return String(url || "")
        .trim()
        .replace(/\/+$/, "");
}
function joinBasePath(baseUrl, route = "") {
    const normalizedBase = normalizeBaseUrl(baseUrl);
    const normalizedRoute = String(route || "")
        .trim()
        .replace(/^\/+/, "");
    if (!normalizedBase) {
        return normalizedRoute ? `/${normalizedRoute}` : "";
    }
    return normalizedRoute
        ? `${normalizedBase}/${normalizedRoute}`
        : normalizedBase;
}
const ROOT_BASE_URL = normalizeBaseUrl(process.env.REACT_APP_BASE_URL || process.env.REACT_APP_API_URL);
function endpointBase(envVarName, fallbackRoute) {
    return (normalizeBaseUrl(process.env[envVarName]) ||
        joinBasePath(ROOT_BASE_URL, fallbackRoute));
}
function normalizeLeadsBaseUrl(baseUrl) {
    const normalizedBaseUrl = normalizeBaseUrl(baseUrl);
    if (!normalizedBaseUrl) {
        return normalizedBaseUrl;
    }
    return normalizedBaseUrl.replace(/\/lists?$/i, "/leads");
}
function rootEndpointBase(envVarName, fallbackRoute) {
    const configured = normalizeBaseUrl(process.env[envVarName]);
    if (configured) {
        return configured;
    }
    try {
        return joinBasePath(new URL(ROOT_BASE_URL).origin, `api/${fallbackRoute}`);
    }
    catch (_error) {
        return joinBasePath(ROOT_BASE_URL, fallbackRoute);
    }
}
export const REACT_APP_BASE_URL = ROOT_BASE_URL;
export const REACT_APP_API_BASE_URL = endpointBase("REACT_APP_API_BASE_URL", "linkedinapi");
export const REACT_APP_USER_API_BASE_URL = endpointBase("REACT_APP_USER_API_BASE_URL", "users");
export const REACT_APP_API_TARGET_ACCOUNT_BASE_URL = endpointBase("REACT_APP_API_TARGET_ACCOUNT_BASE_URL", "target-accounts");
export const REACT_APP_CONTACTS_API_BASE_URL = endpointBase("REACT_APP_CONTACTS_API_BASE_URL", "contacts");
export const REACT_APP_EXCLUSION_API_BASE_URL = endpointBase("REACT_APP_EXCLUSION_API_BASE_URL", "exclusions");
export const REACT_APP_LEADS_API_BASE_URL = normalizeLeadsBaseUrl(endpointBase("REACT_APP_LEADS_API_BASE_URL", "leads"));
export const REACT_APP_LIST_API_BASE_URL = endpointBase("REACT_APP_LIST_API_BASE_URL", "list");
export const REACT_APP_SPARK_PROSPECTS_API_BASE_URL = endpointBase("REACT_APP_SPARK_PROSPECTS_API_BASE_URL", "spark-prospects");
export const REACT_APP_ACCOUNT_CONFIG_API_BASE_URL = endpointBase("REACT_APP_ACCOUNT_CONFIG_API_BASE_URL", "account-configuration");
export const REACT_APP_IMPACTED_POS_API_BASE_URL = endpointBase("REACT_APP_IMPACTED_POS_API_BASE_URL", "impacted-positions");
export const REACT_APP_WEBHOOKS_API_BASE_URL = endpointBase("REACT_APP_WEBHOOKS_API_BASE_URL", "webhooks");
export const REACT_APP_EXECUTIVE_REPORTS_API_BASE_URL = endpointBase("REACT_APP_EXECUTIVE_REPORTS_API_BASE_URL", "executive-reports");
export const REACT_APP_AI_API_BASE_URL = endpointBase("REACT_APP_AI_API_BASE_URL", "ai");
export const REACT_APP_COMPANIES_API_BASE_URL = endpointBase("REACT_APP_COMPANIES_API_BASE_URL", "companies");
export const REACT_APP_INTEGRATIONS_API_BASE_URL = endpointBase("REACT_APP_INTEGRATIONS_API_BASE_URL", "integrations");
export const REACT_APP_AUTH_API_BASE_URL = rootEndpointBase("REACT_APP_AUTH_API_BASE_URL", "auth");
export const REACT_APP_ENRICHMENT_API_BASE_URL = endpointBase("REACT_APP_ENRICHMENT_API_BASE_URL", "enrichment");
export const REACT_APP_OUTREACH_API_BASE_URL = endpointBase("REACT_APP_OUTREACH_API_BASE_URL", "outreach");
export const REACT_APP_ENGAGERS_API_BASE_URL = endpointBase("REACT_APP_ENGAGERS_API_BASE_URL", "engagers");
export const REACT_APP_FILTERS_API_BASE_URL = endpointBase("REACT_APP_FILTERS_API_BASE_URL", "filters");
export const REACT_APP_CRM_CONNECTION_API_BASE_URL = endpointBase("REACT_APP_CRM_CONNECTION_API_BASE_URL", "crm-connection");
function getAccessToken() {
    if (typeof localStorage === "undefined") {
        return "undefined";
    }
    return localStorage.getItem(LOCALSTORAGE_ACCESS_TOKEN) || "undefined";
}
function buildAuthHeaders(extraHeaders = {}) {
    return {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${getAccessToken()}`,
        ...extraHeaders,
    };
}
function shouldRedirectToLoginOnUnauthorized(payload = {}) {
    const errorText = String(payload?.error || "")
        .trim()
        .toLowerCase();
    const codeText = String(payload?.code || "")
        .trim()
        .toUpperCase();
    if (errorText === "access denied" || errorText === "invalid token") {
        return true;
    }
    return ["UNAUTHORIZED", "INVALID_TOKEN", "TOKEN_EXPIRED"].includes(codeText);
}
// Interceptor global para capturar 401
axios.interceptors.response.use((response) => response, (error) => {
    const method = String(error?.config?.method || "unknown").toUpperCase();
    const url = String(error?.config?.url || "unknown");
    const status = error?.response?.status ?? "NO_RESPONSE";
    const code = error?.code || "UNKNOWN";
    if (error.response?.status === 401 &&
        shouldRedirectToLoginOnUnauthorized(error?.response?.data || {}) &&
        typeof window !== "undefined") {
        // Redirecionar para login
        window.location.href = "/login";
    }
    return Promise.reject(error);
});
export function post(url, body, options = {}) {
    return fetchWrapper(url, "POST", body, options);
}
export function put(url, body, options = {}) {
    return fetchWrapper(url, "PUT", body, options);
}
export function patch(url, body, options = {}) {
    return fetchWrapper(url, "PATCH", body, options);
}
export function httpUpload(url, formData, config = {}) {
    const accessToken = getAccessToken();
    return axios.post(url, formData, {
        headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${accessToken}`,
        },
        ...config,
    });
}
export function get(url, options = {}) {
    const { headers: extraHeaders = {}, ...axiosOptions } = options || {};
    return axios.get(url, {
        ...axiosOptions,
        headers: buildAuthHeaders(extraHeaders),
    });
}
export function getWithParams(url, params = {}, options = {}) {
    return get(url, { params, ...options });
}
export function httpDelete(url, body, options = {}) {
    return fetchWrapper(url, "DELETE", body, options);
}
function fetchWrapper(url, method, body, options = {}) {
    const { headers: extraHeaders = {}, ...fetchOptions } = options || {};
    return fetch(url, {
        method,
        // credentials: "include",
        headers: buildAuthHeaders(extraHeaders),
        body: body && JSON.stringify(body),
        ...fetchOptions,
    })
        .then(handleResponse)
        .catch(handleError);
}
async function handleResponse(response) {
    const contentType = response.headers.get("content-type") || "";
    const isJson = contentType.indexOf("application/json") !== -1;
    if (isJson) {
        const responseToJson = await response.json();
        if (response.status === 401 || response.status === 403) {
            if (shouldRedirectToLoginOnUnauthorized(responseToJson)) {
                handleUnauthorized();
                return Promise.reject("Unauthorized.");
            }
            throw responseToJson;
        }
        if (response.ok) {
            return responseToJson;
        }
        throw responseToJson;
    }
    if (response.status === 401 || response.status === 403) {
        handleUnauthorized();
        return Promise.reject("Unauthorized.");
    }
    const responseText = await response.text();
    if (response.ok) {
        return responseText || null;
    }
    const requestError = new Error(responseText || response.statusText || "Request failed");
    requestError.status = response.status;
    throw requestError;
}
function handleUnauthorized() {
    // history.push("/login");
    if (typeof window !== "undefined") {
        window.location.href = "/login";
    }
    return; // ou lance um erro se preferir
}
function handleError(error) {
    if (error?.messages) {
        showErrorToast(error.messages.join(", "));
    }
    if (error instanceof Error) {
        throw error;
    }
    const message = error?.message || error?.error || "Request failed";
    const normalizedError = new Error(message);
    if (error && typeof error === "object") {
        Object.assign(normalizedError, error);
    }
    throw normalizedError;
}
